import { DateTime } from 'luxon';
import { SEVILLE_ZONE } from './clock.ts';

/**
 * Procession days as the Ayuntamiento labels them (`Dia` field), with their offset
 * in days from Easter Sunday. The Madrugá is the night from Thursday to Friday; its
 * processions are on the street from around midnight, so it shares Friday's date.
 */
export const PROCESSION_DAYS = [
  { label: 'Viernes de Dolores', offset: -9 },
  { label: 'Sábado de Pasión', offset: -8 },
  { label: 'Domingo de Ramos', offset: -7 },
  { label: 'Lunes Santo', offset: -6 },
  { label: 'Martes Santo', offset: -5 },
  { label: 'Miércoles Santo', offset: -4 },
  { label: 'Jueves Santo', offset: -3 },
  { label: 'Viernes Madrugada', offset: -2 },
  { label: 'Viernes Santo', offset: -2 },
  { label: 'Sábado Santo', offset: -1 },
  { label: 'Domingo de Resurrección', offset: 0 },
] as const;

export type ProcessionDay = (typeof PROCESSION_DAYS)[number]['label'];
export const PROCESSION_DAY_LABELS = PROCESSION_DAYS.map((d) => d.label) as [
  ProcessionDay,
  ...ProcessionDay[],
];

/** Easter Sunday of `year` (Gregorian calendar, anonymous algorithm), as a Seville date. */
export function easterSunday(year: number): DateTime<true> {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return DateTime.fromObject({ year, month, day }, { zone: SEVILLE_ZONE }) as DateTime<true>;
}

export interface Season {
  year: number;
  /** First day (Viernes de Dolores) and last day (Domingo de Resurrección), ISO dates. */
  first_day: string;
  last_day: string;
  days: { label: ProcessionDay; date: string }[];
}

export function season(year: number): Season {
  const easter = easterSunday(year);
  const days = PROCESSION_DAYS.map(({ label, offset }) => ({
    label,
    date: easter.plus({ days: offset }).toISODate(),
  }));
  return { year, first_day: days[0]!.date, last_day: easter.toISODate(), days };
}

/**
 * Year of the current or next Semana Santa. Until the early hours of Easter Monday
 * (when the last processions are back) it is this year's; afterwards, next year's.
 */
export function seasonYear(now: DateTime): number {
  const local = now.setZone(SEVILLE_ZONE);
  const over = easterSunday(local.year).plus({ days: 1, hours: 6 });
  return local < over ? local.year : local.year + 1;
}
